import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  Wallet, ArrowDownCircle, ArrowUpCircle, Phone, Building2, Loader2,
  CheckCircle2, Clock, AlertCircle, DollarSign, TrendingUp, ShoppingBag, Users, CalendarClock, Settings
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WalletPage = () => {
  const [balance, setBalance] = useState(null);
  const [account, setAccount] = useState(null);
  const [providers, setProviders] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSetup, setShowSetup] = useState(false);
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawing, setWithdrawing] = useState(false);

  // Setup form
  const [setupForm, setSetupForm] = useState({ account_name: '', account_number: '', provider: 'mtn' });
  const [settingUp, setSettingUp] = useState(false);
  // Auto-payout schedule
  const [schedule, setSchedule] = useState(null);
  const [scheduleForm, setScheduleForm] = useState({ frequency: 'disabled', day_of_week: 5, day_of_month: 1, min_amount: 5 });
  const [savingSchedule, setSavingSchedule] = useState(false);

  useEffect(() => {
    const init = async () => {
      try {
        const [bal, acc, prov, hist, sched] = await Promise.all([
          axios.get(`${API}/payouts/balance`),
          axios.get(`${API}/payouts/account`),
          axios.get(`${API}/payouts/momo-providers`),
          axios.get(`${API}/payouts/history`),
          axios.get(`${API}/payout-schedule/config`).catch(() => ({ data: { is_active: false, frequency: 'disabled' } })),
        ]);
        setBalance(bal.data);
        setAccount(acc.data);
        setProviders(prov.data.providers || []);
        setHistory(hist.data.transfers || []);
        setSchedule(sched.data);
        if (sched.data) {
          setScheduleForm({
            frequency: sched.data.frequency || 'disabled',
            day_of_week: sched.data.day_of_week ?? 5,
            day_of_month: sched.data.day_of_month ?? 1,
            min_amount: sched.data.min_amount ?? 5,
          });
        }
      } catch (err) { console.error(err); }
      setLoading(false);
    };
    init();
  }, []);

  const handleSetup = async () => {
    if (!setupForm.account_name.trim() || !setupForm.account_number.trim()) return toast.error('Fill in all fields');
    setSettingUp(true);
    try {
      await axios.post(`${API}/payouts/setup-momo`, setupForm);
      toast.success('MoMo account configured!');
      setShowSetup(false);
      const acc = await axios.get(`${API}/payouts/account`);
      setAccount(acc.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Setup failed'); }
    setSettingUp(false);
  };

  const handleWithdraw = async () => {
    const amt = parseFloat(withdrawAmount);
    if (!amt || amt < 1) return toast.error('Minimum withdrawal: GHS 1');
    setWithdrawing(true);
    try {
      const res = await axios.post(`${API}/payouts/request`, { amount: amt });
      toast.success(`Withdrawal of GHS ${amt} is processing!`);
      setShowWithdraw(false);
      setWithdrawAmount('');
      const [bal, hist] = await Promise.all([
        axios.get(`${API}/payouts/balance`),
        axios.get(`${API}/payouts/history`),
      ]);
      setBalance(bal.data);
      setHistory(hist.data.transfers || []);
    } catch (err) { toast.error(err.response?.data?.detail || 'Withdrawal failed'); }
    setWithdrawing(false);
  };

  const handleSaveSchedule = async () => {
    setSavingSchedule(true);
    try {
      const res = await axios.post(`${API}/payout-schedule/configure`, scheduleForm);
      setSchedule(res.data);
      toast.success(scheduleForm.frequency === 'disabled' ? 'Auto-payouts disabled' : `Auto-payouts set to ${scheduleForm.frequency}`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save schedule'); }
    setSavingSchedule(false);
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-green-500" /></div>;

  return (
    <div className="f500-page max-w-2xl mx-auto space-y-6" data-testid="wallet-page">
      <h1 className="text-3xl sm:text-4xl font-bold text-[var(--isp-heading)] flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center">
          <Wallet className="w-5 h-5 text-white" />
        </div>
        Wallet
      </h1>

      {/* Balance Card */}
      <Card className="p-6  bg-gradient-to-br from-green-500/10 to-emerald-500/10 border-green-500/20" data-testid="balance-card">
        <p className="text-sm text-[var(--isp-body)]">Available Balance</p>
        <p className="text-4xl font-bold text-green-400 mt-1">GHS {(balance?.available || 0).toFixed(2)}</p>
        <div className="grid grid-cols-3 gap-4 mt-4">
          <div className="text-center">
            <ShoppingBag className="w-4 h-4 mx-auto text-emerald-400 mb-1" />
            <p className="text-xs text-[var(--isp-muted)]">Marketplace</p>
            <p className="text-sm font-semibold text-[var(--isp-heading)]">GHS {(balance?.marketplace_earned || 0).toFixed(2)}</p>
          </div>
          <div className="text-center">
            <Users className="w-4 h-4 mx-auto text-blue-400 mb-1" />
            <p className="text-xs text-[var(--isp-muted)]">Tutoring</p>
            <p className="text-sm font-semibold text-[var(--isp-heading)]">GHS {(balance?.tutor_earned || 0).toFixed(2)}</p>
          </div>
          <div className="text-center">
            <ArrowUpCircle className="w-4 h-4 mx-auto text-orange-400 mb-1" />
            <p className="text-xs text-[var(--isp-muted)]">Withdrawn</p>
            <p className="text-sm font-semibold text-[var(--isp-heading)]">GHS {(balance?.withdrawn || 0).toFixed(2)}</p>
          </div>
        </div>
        <div className="flex gap-3 mt-5">
          <Button onClick={() => setShowWithdraw(true)} disabled={!account?.has_account || (balance?.available || 0) < 1}
            className="flex-1 rounded-xl gap-2 bg-gradient-to-r from-green-500 to-emerald-500" data-testid="withdraw-btn">
            <ArrowDownCircle className="w-4 h-4" /> Withdraw
          </Button>
          <Button onClick={() => setShowSetup(true)} variant="outline"
            className="rounded-xl gap-2 border-green-500/30 text-green-300" data-testid="setup-momo-btn">
            <Phone className="w-4 h-4" /> {account?.has_account ? 'Update MoMo' : 'Setup MoMo'}
          </Button>
        </div>
      </Card>

      {/* Account Info */}
      {account?.has_account && (
        <Card className="p-4  bg-[var(--isp-glass)] border-[var(--isp-glass-border)] flex items-center gap-3" data-testid="account-info">
          <Phone className="w-5 h-5 text-green-400" />
          <div>
            <p className="text-sm font-medium text-[var(--isp-heading)]">{account.provider_name}</p>
            <p className="text-xs text-[var(--isp-muted)]">{account.account_name} · {account.account_number_masked}</p>
          </div>
          {account.is_verified ? <CheckCircle2 className="w-5 h-5 text-green-400 ml-auto" /> : <AlertCircle className="w-5 h-5 text-yellow-400 ml-auto" />}
        </Card>
      )}

      {/* Payout History */}
      <div data-testid="payout-history">
        <h3 className="text-sm font-semibold text-[var(--isp-heading)] mb-3">Payout History</h3>
        {history.length === 0 ? (
          <Card className="p-6 text-center  bg-[var(--isp-glass)] border-[var(--isp-glass-border)]">
            <p className="text-xs text-[var(--isp-muted)]">No payouts yet. Start earning by selling notes or tutoring!</p>
          </Card>
        ) : (
          <div className="space-y-2">
            {history.map(t => (
              <Card key={t.id} className="p-3 rounded-xl bg-[var(--isp-glass)] border-[var(--isp-glass-border)] flex items-center gap-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${t.status === 'success' ? 'bg-green-500/20' : t.status === 'pending' ? 'bg-yellow-500/20' : 'bg-red-500/20'}`}>
                  {t.status === 'success' ? <CheckCircle2 className="w-4 h-4 text-green-400" /> : t.status === 'pending' ? <Clock className="w-4 h-4 text-yellow-400" /> : <AlertCircle className="w-4 h-4 text-red-400" />}
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-[var(--isp-heading)]">GHS {t.amount}</p>
                  <p className="text-[10px] text-[var(--isp-muted)]">{t.provider} · {new Date(t.created_at).toLocaleDateString()}{t.is_automated ? ' · Auto' : ''}</p>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full ${t.status === 'success' ? 'bg-green-500/20 text-green-300' : t.status === 'pending' ? 'bg-yellow-500/20 text-yellow-300' : 'bg-red-500/20 text-red-300'}`}>
                  {t.status}
                </span>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Auto-Payout Schedule */}
      <Card className="p-5  bg-[var(--isp-glass)] border-[var(--isp-glass-border)]" data-testid="payout-schedule">
        <div className="flex items-center gap-2 mb-4">
          <CalendarClock className="w-5 h-5 text-[var(--isp-accent)]" />
          <h3 className="text-sm font-semibold text-[var(--isp-heading)]">Auto-Payout Schedule</h3>
          {schedule?.is_active && <span className="px-2 py-0.5 rounded-full bg-[var(--isp-accent)]/15 text-[var(--isp-accent)] text-[10px] font-bold">Active</span>}
        </div>
        <p className="text-xs text-[var(--isp-body)] mb-4">Automatically withdraw earnings on a schedule.</p>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-[var(--isp-body)] mb-1 block">Frequency</label>
            <div className="flex gap-2">
              {['disabled', 'weekly', 'monthly'].map(f => (
                <button key={f} onClick={() => setScheduleForm(p => ({ ...p, frequency: f }))}
                  className={`px-4 py-2 rounded-xl text-xs font-medium capitalize transition-all ${scheduleForm.frequency === f ? 'bg-[var(--isp-accent)]/20 text-[var(--isp-accent)] border border-[var(--isp-accent)]/30' : 'bg-[var(--isp-glass-inner)] border border-[var(--isp-glass-border)] text-[var(--isp-body)] hover:text-[var(--isp-heading)]'}`}
                  data-testid={`schedule-freq-${f}`}>
                  {f === 'disabled' ? 'Off' : f}
                </button>
              ))}
            </div>
          </div>
          {scheduleForm.frequency === 'weekly' && (
            <div>
              <label className="text-xs text-[var(--isp-body)] mb-1 block">Day of Week</label>
              <select value={scheduleForm.day_of_week} onChange={e => setScheduleForm(p => ({ ...p, day_of_week: parseInt(e.target.value) }))}
                className="w-full px-3 py-2 rounded-xl bg-[var(--isp-glass-inner)] border border-[var(--isp-glass-border)] text-sm text-[var(--isp-heading)]" data-testid="schedule-dow">
                {['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'].map((d, i) => (
                  <option key={d} value={i}>{d}</option>
                ))}
              </select>
            </div>
          )}
          {scheduleForm.frequency === 'monthly' && (
            <div>
              <label className="text-xs text-[var(--isp-body)] mb-1 block">Day of Month</label>
              <Input type="number" min={1} max={28} value={scheduleForm.day_of_month}
                onChange={e => setScheduleForm(p => ({ ...p, day_of_month: parseInt(e.target.value) || 1 }))}
                className="bg-[var(--isp-glass-inner)] border-[var(--isp-glass-border)] rounded-xl" data-testid="schedule-dom" />
            </div>
          )}
          {scheduleForm.frequency !== 'disabled' && (
            <div>
              <label className="text-xs text-[var(--isp-body)] mb-1 block">Minimum Amount (GHS)</label>
              <Input type="number" min={1} value={scheduleForm.min_amount}
                onChange={e => setScheduleForm(p => ({ ...p, min_amount: parseFloat(e.target.value) || 5 }))}
                className="bg-[var(--isp-glass-inner)] border-[var(--isp-glass-border)] rounded-xl" data-testid="schedule-min" />
            </div>
          )}
          <Button onClick={handleSaveSchedule} disabled={savingSchedule}
            className="w-full rounded-xl gap-2 bg-gradient-to-r from-green-500 to-emerald-500" data-testid="save-schedule-btn">
            {savingSchedule ? <Loader2 className="w-4 h-4 animate-spin" /> : <Settings className="w-4 h-4" />}
            Save Schedule
          </Button>
        </div>
      </Card>

      {/* Setup MoMo Dialog */}
      <Dialog open={showSetup} onOpenChange={setShowSetup}>
        <DialogContent className="bg-[var(--isp-bg)] border-[var(--isp-glass-border)] text-[var(--isp-heading)] max-w-sm" data-testid="setup-momo-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Phone className="w-5 h-5 text-green-400" /> Setup Mobile Money</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input value={setupForm.account_name} onChange={e => setSetupForm(p => ({ ...p, account_name: e.target.value }))} placeholder="Account Name" className="bg-[var(--isp-glass)] border-[var(--isp-glass-border)]" data-testid="momo-name" />
            <Input value={setupForm.account_number} onChange={e => setSetupForm(p => ({ ...p, account_number: e.target.value }))} placeholder="Phone Number (e.g. 0241234567)" className="bg-[var(--isp-glass)] border-[var(--isp-glass-border)]" data-testid="momo-number" />
            <select value={setupForm.provider} onChange={e => setSetupForm(p => ({ ...p, provider: e.target.value }))} className="w-full px-3 py-2 rounded-xl bg-[var(--isp-glass)] border border-[var(--isp-glass-border)] text-sm" data-testid="momo-provider">
              {providers.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <Button onClick={handleSetup} disabled={settingUp} className="w-full rounded-xl bg-gradient-to-r from-green-500 to-emerald-500" data-testid="momo-submit">
              {settingUp ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Account'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Withdraw Dialog */}
      <Dialog open={showWithdraw} onOpenChange={setShowWithdraw}>
        <DialogContent className="bg-[var(--isp-bg)] border-[var(--isp-glass-border)] text-[var(--isp-heading)] max-w-sm" data-testid="withdraw-dialog">
          <DialogHeader><DialogTitle>Withdraw Funds</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-[var(--isp-muted)]">Available: <span className="text-green-400 font-bold">GHS {(balance?.available || 0).toFixed(2)}</span></p>
            <Input type="number" value={withdrawAmount} onChange={e => setWithdrawAmount(e.target.value)} placeholder="Amount (GHS)" min="1" className="bg-[var(--isp-glass)] border-[var(--isp-glass-border)]" data-testid="withdraw-amount" />
            <Button onClick={() => setWithdrawAmount(String(balance?.available || 0))} variant="outline" size="sm" className="rounded-lg text-xs border-green-500/30 text-green-300">Withdraw All</Button>
            <Button onClick={handleWithdraw} disabled={withdrawing} className="w-full rounded-xl bg-gradient-to-r from-green-500 to-emerald-500" data-testid="withdraw-submit">
              {withdrawing ? <Loader2 className="w-4 h-4 animate-spin" /> : `Withdraw GHS ${withdrawAmount || '0'}`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WalletPage;
